import {
  Box,
  Button,
  createStyles,
  Group,
  NumberInput,
  NumberInputHandlers,
  NumberInputProps,
  Text,
} from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import React, { useRef } from "react";
import { IconV3, RpiAlert, RpiLabelText } from "@rpi/openapi-core";

const useStyles = createStyles((_theme, { fullWidth }: { fullWidth: boolean }) => ({
  header: {
    marginBottom: "var(--codex-spacing-2)",
  },
  numberInputRoot: {
    flex: fullWidth ? 1 : undefined,
  },
  descriptionText: {
    fontFamily: tokens.font.family.sans,
    fontSize: "var(--codex-font-size-sm)",
    color: tokens.color.semantic.text.subtle,
  },
  input: {
    backgroundColor: tokens.color.primitive.base.white,
    padding: "var(--codex-spacing-2)",
    color: tokens.color.semantic.text.bold,
    textAlign: "center",
    fontSize: "var(--codex-font-size-base)",
    fontFamily: tokens.font.family.sans,
    "font-weight": tokens.font.weight.normal,
    lineHeight: "var(--codex-font-line-height-normal)",
    borderColor: tokens.color.semantic.border.subtle,
    borderRadius: 0,
    borderTop: 0,
    borderBottom: 0,
    "&:hover": {
      background: tokens.color.semantic.bg.inputHover,
    },
    "&:disabled": {
      backgroundColor: tokens.color.semantic.bg.neutralDisabled,
      color: tokens.color.semantic.text.disabled,
      opacity: "unset",
    },
    "&[data-error]": {
      borderColor: tokens.color.semantic.border.danger,
    },
  },
  errorContainer: {
    display: "flex",
    gap: 4,
    alignItems: "start",
  },
  errorIcon: {
    lineHeight: "inherit",
  },
  stepButton: {
    border: 0,
    color: tokens.color.semantic.icon.bold,
    "&:hover": {
      background: tokens.color.semantic.bg.inputHover,
    },
    "&:active": {
      background: tokens.color.semantic.bg.neutralActive,
      transform: "none",
    },
  },
  stepperStyle: {
    display: fullWidth ? "flex" : "inline-flex",
    width: fullWidth ? "100%" : undefined,
    flexWrap: "nowrap",
    border: `1px solid ${tokens.color.semantic.border.subtle}`,
    borderRadius: "var(--codex-radius-base)",
  },
}));

interface RpaNumberStepperInputProps extends Omit<NumberInputProps, "label"> {
  label: string;
  error?: string;
  fullWidth?: boolean;
}

export const RpaNumberStepperInput: React.FC<RpaNumberStepperInputProps> = ({
  label,
  description,
  error,
  fullWidth = false,
  ...props
}) => {
  const { classes } = useStyles({ fullWidth });
  const handlersRef = useRef<NumberInputHandlers>();

  return (
    <Box>
      <Group className={classes.header}>
        <RpiLabelText>{label}</RpiLabelText>
        <Text className={classes.descriptionText}>{description}</Text>
      </Group>
      <Group spacing={0} className={classes.stepperStyle}>
        <Button
          size="md"
          aria-label="Decrease"
          className={classes.stepButton}
          variant="default"
          onClick={() => handlersRef.current?.decrement()}
        >
          <IconV3 icon="remove" color="inherit" />
        </Button>
        <NumberInput
          size="md"
          classNames={{
            root: classes.numberInputRoot,
            input: classes.input,
          }}
          aria-label={label}
          data-error={!!error || undefined}
          {...props}
          handlersRef={handlersRef}
        />
        <Button
          size="md"
          aria-label="Increase"
          className={classes.stepButton}
          variant="default"
          onClick={() => handlersRef.current?.increment()}
        >
          <IconV3 icon="add" color="inherit" />
        </Button>
      </Group>
      {error && <RpiAlert>{error}</RpiAlert>}
    </Box>
  );
};
